/**
 * Messung in der Anwendung — und zwar nur die eine, die zählt.
 *
 * The website counts who reads what. Behind the login the arrangement is the
 * opposite: no page views, no screen names, no path through the bookkeeping.
 * Two events are sent, and they are the two steps the advertising is judged by
 * — a registration and the subscription it may turn into. Both happen on
 * `app.easyrechnung.at`, so `easyrechnung.at` cannot observe either.
 * Everything else a report might want about the application is either nobody's
 * business or already in the database.
 *
 * That restraint is not politeness. `docs/DATENSCHUTZ.md` says what runs
 * behind the login, and every event added here has to be added there first —
 * with the customer's data in the same browser tab, "we only send an event"
 * is a sentence that has to stay literally true.
 *
 * Nothing sent from here carries an identifier: no e-mail address, no user id,
 * no tenant id, no invoice number and no amount. Google would receive a
 * customer of a customer, which no consent of ours could cover.
 *
 * The gate: `window.gtag` does not exist until `useConsent().accept()` runs.
 * One place creates it, one place uses it, and a customer who said no leaves
 * nothing behind — the same shape the website uses, for the same reason.
 */
import { ADS_CONVERSION_ID, GA4_MEASUREMENT_ID, adsSendTo, toEuros } from '@eua/shared'

/** The function Google's snippet puts on `window`. */
export type Gtag = (...args: unknown[]) => void

type Win = Window & { dataLayer?: unknown[]; gtag?: Gtag }

/**
 * The property, overridable for a test build.
 *
 * Empty switches the measurement off while the question is still asked
 * honestly — a copy of the application that must not measure sets
 * `VITE_GA4_ID=` and loads nothing.
 */
export const MEASUREMENT_ID =
  import.meta.env.VITE_GA4_ID === undefined ? GA4_MEASUREMENT_ID : import.meta.env.VITE_GA4_ID

/**
 * Wer die Messung zur Probe auslösen darf.
 *
 * Einstellungen → GA4-Test lets this one address fire the two events by hand
 * and watch them arrive. The alternative is registering a throwaway account
 * and paying for a subscription in order to find out that a tag was misspelt,
 * which is how a broken measurement stays broken for a quarter.
 *
 * It is not a customer feature and it is not a permission. Everything the page
 * sends lands in the same property the real events land in, so an event fired
 * by somebody else would be a wrong number in the report that decides where
 * advertising money goes. Nothing secret hangs on the check: the page sends
 * the same events the application sends anyway, from the same browser, with
 * the same measurement ID that is in every request a consenting visitor makes.
 * The gate keeps the page out of everybody else's way; it guards a report, not
 * a secret.
 *
 * Compared lowercased and trimmed, because that is the shape the API stores.
 */
export const MEASUREMENT_TEST_USER = 'florian@untermoser.com'

/** Whether this address may open the GA4 test page. Nobody else may. */
export function mayTestMeasurement(email: string | null | undefined): boolean {
  return typeof email === 'string' && email.trim().toLowerCase() === MEASUREMENT_TEST_USER
}

/** Every event the application may send. Two, and it stays two until the documents say otherwise. */
export const APP_EVENTS = {
  /**
   * Somebody registered. `sign_up` is Google's own name for it — the same
   * reasoning as `purchase` below: a name Analytics and Ads already know
   * beats one that has to be declared by hand in two interfaces first.
   */
  sign_up: 'sign_up',
  /**
   * The same two moments, addressed to the Google Ads account instead of the
   * Analytics property. `send_to` decides which of the two hears it; see
   * `reportToAds`.
   */
  conversion: 'conversion',
  /**
   * A paid subscription came about. Sent on the return from a paid checkout.
   *
   * `purchase` is Google's own name for it, not one of ours. Analytics and Ads
   * both know it: as a custom name the same event would need a conversion to
   * be defined by hand in two interfaces, neither of which is in this
   * repository, and until someone does that the number simply is not there.
   */
  purchase: 'purchase',
} as const

export type AppEvent = (typeof APP_EVENTS)[keyof typeof APP_EVENTS]

/**
 * Loads `gtag.js`. Called from the consent composable after the yes, and from
 * nowhere else.
 *
 * Consent Mode v2 defaults go onto the queue before the script is requested,
 * so the order Google wants is a property of the sequence rather than
 * something a later edit can get wrong.
 *
 * Granted are `analytics_storage`, `ad_storage` and `ad_user_data`; denied
 * stays `ad_personalization`, and that is the line the whole arrangement is
 * drawn on. The first three let a registration be traced back to the
 * advertisement that was clicked three days earlier — without them Google
 * estimates that number instead of counting it, and an estimate is a poor
 * thing to spend on. The fourth would hand Google an audience to advertise to
 * elsewhere; nothing here wants that, `docs/DATENSCHUTZ.md` says so, and this
 * flag is what keeps the sentence true.
 *
 * `send_page_view: false`, because there is no page view to send. See above.
 */
export function loadAnalytics(): void {
  if (!MEASUREMENT_ID) return
  const w = window as Win
  if (w.gtag) return

  w.dataLayer = w.dataLayer || []
  // `push(arguments)`, not `push([...])`: `gtag.js` reads a queued entry as a
  // command only when it is an `arguments` object. A plain array is stored as
  // data, nothing throws, and the consent state below simply never arrives.
  const gtag: Gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  }
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  })

  gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
  })
  /*
   * Die Kennung der Anzeige reist notfalls in der Adresse mit.
   *
   * Safari deletes a cookie written by a script after seven days. Somebody who
   * clicks an advertisement, tries the product and subscribes in the second
   * week has no `_gcl_au` left by then, and the advertisement that brought
   * them looks like it produced nothing. With this, the identifier is carried
   * in the link between the pages instead, and the conversion still finds its
   * click.
   *
   * `ads_data_redaction` is Google's companion setting and is deliberately not
   * here: it strips click identifiers from requests made while `ad_storage` is
   * denied, and in this application no request is ever made in that state —
   * this function runs after the yes and nowhere else.
   */
  gtag('set', 'url_passthrough', true)
  gtag('js', new Date())
  gtag('config', MEASUREMENT_ID, { send_page_view: false })
  // The Ads account, when there is one. A second destination, not a second
  // script: the same `gtag.js` serves both.
  if (ADS_CONVERSION_ID) gtag('config', ADS_CONVERSION_ID)
  w.gtag = gtag

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`
  document.head.appendChild(script)
}

/** Whether `gtag.js` has been set up in this tab — that is, whether a yes was given. */
export function isMeasuring(): boolean {
  return typeof (window as Win).gtag === 'function'
}

/**
 * Schaltet die DebugView der Property für diesen Tab ein oder aus.
 *
 * Without it a correctly sent event stays invisible for up to a day: GA4 puts
 * unflagged traffic into the reports, and the reports are processed overnight.
 * `debug_mode` puts the same event into DebugView as well, where it shows up
 * within seconds with every parameter it carried — which is the entire point
 * of sending one by hand.
 *
 * A second `config` for the same property updates the first one. It loads no
 * second script and starts no second measurement, and `send_page_view` is
 * repeated because a `config` states the whole configuration rather than a
 * change to it. Only the GA4 test page calls this; the setting holds until the
 * tab is reloaded.
 */
export function setDebugMode(on: boolean): void {
  const w = window as Win
  if (!w.gtag || !MEASUREMENT_ID) return
  w.gtag('config', MEASUREMENT_ID, { send_page_view: false, debug_mode: on })
}

/*
 * Events from before the question was answered.
 *
 * A registration happens in the first minute of a visit, usually while the bar
 * at the bottom is still unanswered — dropped outright, the one event that says
 * where a customer came from would almost never be sent. So it waits here, in
 * memory, and goes out if the answer is yes. On a no, or on no answer at all,
 * the array dies with the tab and nothing is ever sent or stored. The cap is
 * there so a long session cannot grow it without bound; two events fit in it
 * many times over.
 */
const pending: { event: AppEvent; parameters: Record<string, unknown> }[] = []
const PENDING_MAX = 5

/**
 * Sends an event, or holds it until the customer has answered the question.
 *
 * Every event says where it goes. Two destinations can be configured — the
 * Analytics property and, later, the Ads account — and `gtag.js` broadcasts an
 * event that names neither to both of them. The subscription would then arrive
 * in Ads twice: once as `purchase`, once as the conversion below. So the
 * measurement addresses the property and `reportToAds` addresses the account,
 * and neither hears the other's traffic.
 */
export function track(event: AppEvent, parameters: Record<string, unknown> = {}): void {
  const addressed = { send_to: MEASUREMENT_ID, ...parameters }
  const w = window as Win
  if (!w.gtag) {
    if (pending.length < PENDING_MAX) pending.push({ event, parameters: addressed })
    return
  }
  flush()
  w.gtag('event', event, addressed)
}

/**
 * Sends what was held while the customer was still deciding.
 *
 * `useConsent` calls this the moment it has set `window.gtag`. Without it the
 * queue would only drain on the *next* event, and a registration that leads to
 * no subscription that day has no next event.
 */
export function flush(): void {
  const w = window as Win
  if (!w.gtag) return
  while (pending.length) {
    const held = pending.shift()!
    w.gtag('event', held.event, held.parameters)
  }
}

/**
 * Eine Registrierung.
 *
 * No parameters. GA4 offers `method` for the way somebody signed up, and there
 * is exactly one way here — a form on `app.easyrechnung.at` — so the field
 * would carry a constant. Nothing about the person, the business or the e-mail
 * address travels with it; see the note at the top of this file.
 */
export function reportRegistration(): void {
  track(APP_EVENTS.sign_up)
  reportToAds('sign_up')
}

/**
 * Dasselbe Ereignis noch einmal, an das Werbekonto.
 *
 * Analytics and Ads are two destinations, and an event sent to one is not in
 * the other unless the accounts are linked and the import is set up. This is
 * the direct way: it reaches Ads while the click identifier is still in the
 * browser, which is what makes an attribution rather than an estimate.
 *
 * Silent while `ADS_CONVERSION_ID` is empty. Nothing is sent, nothing is
 * configured, and the day the account exists this starts working by itself.
 */
function reportToAds(event: 'sign_up' | 'purchase', parameters: Record<string, unknown> = {}): void {
  const sendTo = adsSendTo(event)
  if (!sendTo) return
  // `conversion` is what Ads listens for; the destination decides which
  // conversion action it lands in.
  track(APP_EVENTS.conversion, { ...parameters, send_to: sendTo })
}

/** What the purchase event carries. Both values come from the return itself. */
export interface Purchase {
  /**
   * Stripe's checkout session, from the return address. Analytics uses it to
   * recognise a purchase it has already counted, which is the whole reason it
   * is passed at all. It names a payment, not a person, and Google receives no
   * other identifier from this application.
   */
  transactionId: string | null
  /** Net price of the subscription in cents, as the API states it. */
  netCents: number | null
}

/**
 * Der Abschluss eines Abos, gemeldet als `purchase`.
 *
 * Called from `views/settings/SubscriptionView.vue` when the customer returns
 * from a paid checkout and the subscription is actually on the tenant. That
 * view drops the markers from the URL as it reads them, so a reload of the
 * return address cannot report the same subscription twice — and Analytics
 * drops a repeat of the same `transaction_id` even if it somehow does.
 *
 * The amount is the price of the subscription, net, without tax; it is what
 * the advertising is judged by and it is a published figure, the same one on
 * every page of `easyrechnung.at`. Nothing else travels: no name, no e-mail
 * address, no user or tenant id, and nothing from the bookkeeping.
 *
 * Die Google-Ads-Conversion gehört ebenfalls hierher, sobald sie eingerichtet
 * ist. It needs three things first, none of which is code: the conversion ID
 * from the Ads account, `ad_storage` granted above — it is `denied` today —
 * and the sentence in `docs/DATENSCHUTZ.md` that says advertising success is
 * measured. Until then a `send_to: 'AW-…'` would be a request Google answers
 * and nobody can read.
 */
export function reportSubscription(purchase: Purchase): void {
  reportToAds('purchase', {
    ...(purchase.transactionId ? { transaction_id: purchase.transactionId } : {}),
    ...(purchase.netCents === null
      ? {}
      : { currency: 'EUR', value: toEuros(purchase.netCents) }),
  })
  track(APP_EVENTS.purchase, {
    ...(purchase.transactionId ? { transaction_id: purchase.transactionId } : {}),
    ...(purchase.netCents === null
      ? {}
      : {
          currency: 'EUR',
          value: toEuros(purchase.netCents),
          items: [{ item_id: 'abo', item_name: 'easyrechnung Jahresabo', quantity: 1 }],
        }),
  })
}
